import { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { type Board } from '@air/api/types';
import { Portal } from '@air/primitive-portal';
import { useAirModal } from '@air/provider-modal';
import { useShortIdContext } from '@air/provider-short-id';
import { resetSelectedItemsAction, selectedItemsSelector } from '@air/redux-selected-items';

import { SharePublicBoardModal } from '~/components/Modals/SharePublicBoardModal';
import { SharePublicClipModal } from '~/components/Modals/SharePublicClipModal';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { BoardsAndAssetsSelectionBar } from '~/components/v2/components/shared/selectionBar/BoardsAndAssetsSelectionBar';
import { useSelectedItemsSummary } from '~/components/v2/components/shared/selectionBar/hooks/useSelectedItemsSummary';
import { getSelectionActionBarItemCountState } from '~/components/v2/components/shared/selectionBar/selectionBarItemCountState';
import { usePublicSelectionBarMenu } from '~/components/v2/hooks/usePublicSelectionBarMenu';
import { useSelectionBarDownloads } from '~/components/v2/hooks/useSelectionBarDownloads';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { useBulkDownloadPublicAssets } from '~/hooks/useBulkDownloadPublicAssets';
import { useDownloadPublicAsset } from '~/hooks/useDownloadPublicAsset';
import { useDownloadPublicBoard } from '~/hooks/useDownloadPublicBoard';
import { useDownloadPublicItems } from '~/hooks/useDownloadPublicItems';
import { usePublicSelectAllTotalCount } from '~/hooks/usePublicSelectAllTotalCount';
import { hasOnlyCanvasesSelectedSelector } from '~/store/selectedItems/selectors';
import { getPublicSubboardShareUrl } from '~/swr-hooks/shortUrl/utils';
import { getBoardIdFromPath } from '~/utils/PathUtils';
import { canDownloadVersion } from '~/utils/permissions/assetPermissions';
import { canGenerateZip } from '~/utils/permissions/taskPermissions';

export const PublicBoardsAndAssetsSelectionBar = memo(() => {
  const dispatch = useDispatch();
  const selectedItems = useSelector(selectedItemsSelector);
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const resetSelectAll = useViewsSelectAllStore((state) => state.resetSelectAll);
  const selectAllTotalCount = usePublicSelectAllTotalCount();

  const onClearAll = useCallback(() => {
    resetSelectAll();
    dispatch(resetSelectedItemsAction());
  }, [dispatch, resetSelectAll]);

  const hasSelection = selectedItems.length > 0 || selectAll;
  const hasOnlyCanvasesSelected = useSelector(hasOnlyCanvasesSelectedSelector);
  const itemCount = useMemo(
    () =>
      getSelectionActionBarItemCountState({
        hasOnlyCanvasesSelected,
        selectAll,
        selectedItemCount: selectedItems.length,
      }),
    [hasOnlyCanvasesSelected, selectAll, selectedItems.length],
  );

  const { summary } = useSelectedItemsSummary({ selectAllTotalCount });

  // Permissions and capabilities
  const { permissions } = usePublicPermissions();
  const canDownload = canGenerateZip(permissions);
  const canDownloadAsset = canDownloadVersion(permissions);

  // Download handlers using shared hook
  const { createBulkDownloadHandler, createAssetDownloadHandler, createBoardDownloadHandler } =
    useSelectionBarDownloads();
  const { downloadPublicItems } = useDownloadPublicItems();
  const { downloadPublicAsset } = useDownloadPublicAsset();
  const { downloadPublicBoard } = useDownloadPublicBoard();
  const { bulkDownloadPublicAssets } = useBulkDownloadPublicAssets();

  // Modals
  const [showSharePublicBoardModal] = useAirModal(SharePublicBoardModal);
  const [showSharePublicClipModal] = useAirModal(SharePublicClipModal);

  // Context
  const { shortId } = useShortIdContext();

  // Create download handlers
  const handleBulkDownload = createBulkDownloadHandler({
    downloadItems: downloadPublicItems,
    bulkDownloadAssets: bulkDownloadPublicAssets,
    additionalParams: { shortId },
  });

  const handleAssetDownload = createAssetDownloadHandler(downloadPublicAsset, { shortId });
  const handleBoardDownload = createBoardDownloadHandler(downloadPublicBoard);

  // Helper functions for single item actions
  const handleBoardShare = useCallback(
    (board: Board) => {
      showSharePublicBoardModal({
        id: board.id,
        url: getPublicSubboardShareUrl(shortId, board.id),
        trackLocation: 'action-bar',
      });
    },
    [showSharePublicBoardModal, shortId],
  );

  const handleClipShare = useCallback(
    (clipId: string) => {
      const boardId = getBoardIdFromPath(window.location.pathname);

      if (boardId) {
        showSharePublicClipModal({
          id: clipId,
          url: getPublicSubboardShareUrl(shortId, boardId),
          trackLocation: 'action-bar',
        });
      }
    },
    [showSharePublicClipModal, shortId],
  );

  const { menuOptions, SuffixBarButtons } = usePublicSelectionBarMenu({
    canDownload,
    canDownloadAsset,
    handleBulkDownload,
    handleBoardDownload,
    handleBoardShare,
    handleAssetDownload,
    handleClipShare,
  });

  return (
    <Portal>
      <div className="fixed inset-x-2 bottom-2">
        <BoardsAndAssetsSelectionBar
          hasSelection={hasSelection}
          itemCount={itemCount}
          menuOptions={selectAll ? [] : menuOptions}
          onClearAll={onClearAll}
          suffixBarButtons={selectAll ? undefined : <SuffixBarButtons />}
          summary={summary}
        />
      </div>
    </Portal>
  );
});

PublicBoardsAndAssetsSelectionBar.displayName = 'PublicBoardsAndAssetsSelectionBar';
